import { useForm } from "react-hook-form";
import { GrupoAcessoForm, GrupoAcessoFormDefaultValues, GrupoAcessoFormSchema } from "../schemas/grupos_acesso_form";
import { useCallback, useRef, useState } from "react";
import { GestorGrupo } from "../../domain/models/gestor_grupo";
import { useLocation, useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { AtribuicaoGrupo } from "../../domain/models/atribuicao_grupo";
import { useGrupoAcessoCadastroPagination } from "../hooks/grupos_acesso_cadastro/pagination";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

/**
 * useGruposAcessoCadastroViewModel
 */

export const useGruposAcessoCadastroViewModel = () => {
  const {
    administracao: {
      getGrupoAcessoUseCase,
      getAllAtribuicaoListaUseCase,
      createGrupoAcessoUseCase,
      updateGrupoAcessoUseCase,
      deleteGrupoAcessoUseCase,

    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    }
  } = useDependencyInjection();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const hookForm = useForm<GrupoAcessoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: GrupoAcessoFormDefaultValues,
    resolver: zodResolver(GrupoAcessoFormSchema),
  });


  const menuRef = useRef<HTMLButtonElement>(null);
  const [openMenu, setOpenMenu] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [selectedIdGrupoAcesso, setSelectedIdGrupoAcesso] = useState<number>();
  const [gestoresGrupo, setGestoresGrupo] = useState<GestorGrupo[]>([]);
  const [selectedGestorGrupo, setSelectedGestorGrupo] = useState<GestorGrupo | null>(null);

  const [atribuicaoGrupoLista, setAtribuicaoGrupoLista] = useState<AtribuicaoGrupo[] | []>();

  const [atribuicaoGrupoSelectionModel, setAtribuicaoGrupoSelectionModel] =
    useState<GridRowSelectionModel>();

  const pagination = useGrupoAcessoCadastroPagination();


  //#region CARREGAMENTO

  const loadAtribuicaoGrupo = useCallback(async (funcState?: { page?: number, rowsPerPage?: number }, idsGrupoAcesso?: number[]) => {
    try {
      setLoading(true);
      await getAllAtribuicaoListaUseCase.execute(
        funcState?.page ?? 1,
        funcState?.rowsPerPage ?? 100,
      );
      setAtribuicaoGrupoLista(getAllAtribuicaoListaUseCase.items);
      pagination.setTotalItems(getAllAtribuicaoListaUseCase.totalItems);
      pagination.setRowsPerPage(funcState?.rowsPerPage ?? 100);
      pagination.setPage(funcState?.page ?? 1);

      if (idsGrupoAcesso && idsGrupoAcesso.length > 0) {
        setAtribuicaoGrupoSelectionModel({ type: 'include', ids: new Set(idsGrupoAcesso) });
      }

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar atribuições de grupo!'));
    } finally {
      setLoading(false);
    }
  }, [
    getAllAtribuicaoListaUseCase,
    pagination,
    pushErrorNotification
  ]);

  const loadGrupoAcesso = useCallback(async (idGrupoAcesso?: number) => {
    try {
      setLoading(true);
      setErrorMessage(undefined);

      if (idGrupoAcesso !== undefined) {
        const grupoAcesso = await getGrupoAcessoUseCase.getGrupoAcesso(idGrupoAcesso);

        hookForm.setValue('ativo', grupoAcesso.ativo ?? false);
        hookForm.setValue('nomeGrupo', grupoAcesso.nomeGrupo ?? "");
        hookForm.setValue('descricao', grupoAcesso.descricao ?? "");
        hookForm.setValue('gestorGrupo', grupoAcesso.idFuncionario ?? null);

        if (grupoAcesso.idsAtribuicaoGrupo && grupoAcesso.idsAtribuicaoGrupo.length > 0) {
          setAtribuicaoGrupoSelectionModel({ type: 'include', ids: new Set(grupoAcesso.idsAtribuicaoGrupo) });
        }

        loadAtribuicaoGrupo(undefined, grupoAcesso.idsAtribuicaoGrupo ?? []);
        setSelectedIdGrupoAcesso(idGrupoAcesso);

      } else {
        setSelectedIdGrupoAcesso(undefined);
      }
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar grupo de acesso!'));
    } finally {
      setLoading(false);
    }
  }, [
    getGrupoAcessoUseCase,
    hookForm,
    pushErrorNotification,

  ]);

  //#endregion CARREGAMENTO



  //#region CABEÇALHO


  const handleNewGrupo = useCallback(() => {
    navigate(location.pathname, { replace: true, state: {} });
    hookForm.reset(GrupoAcessoFormDefaultValues);
    setSelectedIdGrupoAcesso(undefined);
    setAtribuicaoGrupoSelectionModel(undefined);
    setSelectedGestorGrupo(null);
    setOpenMenu(false);

  }, [navigate, location.pathname, hookForm]);


  const handlePageBack = useCallback(() => {
    navigate('/GruposAcesso_lista');
  }, [navigate]);


  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      if (selectedIdGrupoAcesso !== undefined)
        await loadGrupoAcesso(selectedIdGrupoAcesso!);
      else
        handleNewGrupo();
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar grupo de acesso!'));
    } finally {
      setLoading(false);
    }
  }, [loadGrupoAcesso]);

  const handleCloseMenu = useCallback((event: any) => {
    if (
      menuRef.current &&
      menuRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpenMenu(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    setOpenMenu((prev) => !prev);
  }, []);

  const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenMenu(false);
    } else if (event.key === 'Escape') {
      setOpenMenu(false);
    }
  }, []);

  //#endregion



  //#region CADASTRO/UPDATE
  const handleGrupoAcessoCadastroSubmit = useCallback(async (form: GrupoAcessoForm) => {
    try {
      setSaving(true);
      if (selectedIdGrupoAcesso !== undefined) {

        await updateGrupoAcessoUseCase.execute(
          selectedIdGrupoAcesso,
          form.nomeGrupo,
          form.descricao,
          form.ativo,
          form.gestorGrupo ?? null,

          form.idsAtribuicaoGrupo ? form.idsAtribuicaoGrupo.map(x => x.idAtribuicao) : []
        );

        await loadGrupoAcesso(selectedIdGrupoAcesso);
        pushInfoNotification("Sucesso", "Grupo de acesso alterado com sucesso!", { isAutoFinish: true });

      } else {

        const newIdGrupoAcesso = await createGrupoAcessoUseCase.execute(
          form.nomeGrupo,
          form.descricao ?? null,
          form.gestorGrupo ?? null,
          form.idsAtribuicaoGrupo ? form.idsAtribuicaoGrupo.map(x => x.idAtribuicao) : []

        );
        setSelectedIdGrupoAcesso(newIdGrupoAcesso)
        await loadGrupoAcesso(newIdGrupoAcesso);

        pushInfoNotification("Sucesso", "Grupo de acesso criado com sucesso!", { isAutoFinish: true });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar grupo de acesso!"));
    } finally {
      setSaving(false);
    }
  }, [
    selectedIdGrupoAcesso,
    createGrupoAcessoUseCase,
    updateGrupoAcessoUseCase,
    loadGrupoAcesso,
    pushInfoNotification,
    pushErrorNotification,
  ]);


  //#endregion

  //#region EXCLUSÃO

  const handleGrupoAcessoDeleteOpenState = useCallback((openState: boolean) => {

    if (selectedIdGrupoAcesso === undefined) {
      pushWarnNotification("Atenção", "Nenhum grupo de acesso selecionado para exclusão!");
      return;
    }
    if (openState) {
      setOpenDeleteModal(openState)
    } else {
      setOpenDeleteModal(openState)
    }
  }, [selectedIdGrupoAcesso, pushWarnNotification]);


  const handleConfirmarDeletarGrupoAcesso = useCallback(async () => {
    try {
      if (selectedIdGrupoAcesso !== undefined) {
        await deleteGrupoAcessoUseCase.execute(selectedIdGrupoAcesso);
        pushInfoNotification("Sucesso", "Grupo de acesso deletado com sucesso!");
        navigate('/GruposAcesso_lista');
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao deletar grupo de acesso!"));
    } finally {
      setOpenDeleteModal(false);
      setSelectedIdGrupoAcesso(undefined);
    }
  }, [
    selectedIdGrupoAcesso,
    deleteGrupoAcessoUseCase,
    pushInfoNotification,
    pushErrorNotification,
    navigate]);


  //#endregion







  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      const idGrupoAcesso = selectedIdGrupoAcesso ?? location.state?.idGrupoAcesso as number
      if (idGrupoAcesso !== undefined) {
        setSelectedIdGrupoAcesso(idGrupoAcesso);
        await loadGrupoAcesso(idGrupoAcesso);
        await loadAtribuicaoGrupo();
      } else {
        hookForm.reset(GrupoAcessoFormDefaultValues);
        await loadAtribuicaoGrupo();

      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar grupo de acesso!")[0]);
    }

  });

  //#endregion





  //#region TABELA ATRIBUICAO
  const handleAtribuicaoSelectionModelChange = useCallback((selectionModels: GridRowSelectionModel) => {

    if (selectionModels.type === 'exclude' && selectionModels.ids.size === 0) {
      // Todos os itens estão selecionados
      const todosIds = atribuicaoGrupoLista?.map(x => ({ idAtribuicao: x.idAtribuicao })) ?? [];
      hookForm.setValue('idsAtribuicaoGrupo', todosIds);
      setAtribuicaoGrupoSelectionModel({
        type: 'include',
        ids: new Set(todosIds.map(x => x.idAtribuicao)),
      });

    } else {
      hookForm.setValue('idsAtribuicaoGrupo', [...selectionModels.ids.values()].map(x => ({ idAtribuicao: typeof x === 'number' ? x : Number(x) })));
      setAtribuicaoGrupoSelectionModel(selectionModels);
    }

  }, [hookForm, atribuicaoGrupoLista]);


  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadAtribuicaoGrupo({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de atribuições!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadAtribuicaoGrupo]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);
      await loadAtribuicaoGrupo({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de atribuições!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadAtribuicaoGrupo]);

  //#endregion


  //#region HISTORICO

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (true) {
        changeHistory.handleOpenChangeHistory(selectedIdGrupoAcesso ?? 0, LogTipoTabela.GRUPO_ACESSO, true);
      }
    }
  }, [
    selectedIdGrupoAcesso,
    changeHistory,
  ]);

  //#endregion


  return ({
    loading,
    hookForm,
    gestoresGrupo,
    selectedGestorGrupo,
    errorMessage,
    saving,
    openDeleteModal,
    atribuicaoGrupoLista,
    atribuicaoGrupoSelectionModel,
    pagination,
    changeHistory,
    menuRef,
    openMenu,

    handleCloseMenu,
    handleToggleMenu,
    handleListKeyDown,
    handleGrupoAcessoCadastroSubmit: hookForm.handleSubmit(handleGrupoAcessoCadastroSubmit),
    handleGrupoAcessoDeleteOpenState,
    handleConfirmarDeletarGrupoAcesso,
    handleAtribuicaoSelectionModelChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleNewGrupo,
    handlePageBack,
    handleReload,
    handleOpenChangeHistory,

  });
}